import Dexie, { type EntityTable } from 'dexie'
import type { BoggleLocalProgressStore } from './localProgressStore'
import type {
  BoggleBadgeProgressProjection,
  BoggleCompletedRun,
  BoggleUnviewedAchievementTier,
  BoggleUnviewedBadge,
  BoggleUnviewedWord,
  BoggleWordOccurrence,
  BoggleWordProjectionSnapshot,
  BoggleWordStat,
  PlayerAchievementState,
} from './types'

const BOGGLE_PROGRESS_DATABASE_NAME = 'boggle-progress'
const BOGGLE_BADGE_PROGRESS_PROJECTION_ID = 'default'
const BOGGLE_PLAYER_ACHIEVEMENT_STATE_ID = 'default'

type BoggleBadgeProgressProjectionRow = BoggleBadgeProgressProjection & {
  id: typeof BOGGLE_BADGE_PROGRESS_PROJECTION_ID
}

type PlayerAchievementStateRow = PlayerAchievementState & {
  id: typeof BOGGLE_PLAYER_ACHIEVEMENT_STATE_ID
}

type BoggleProgressDatabase = Dexie & {
  badgeProgress: EntityTable<BoggleBadgeProgressProjectionRow, 'id'>
  completedRuns: EntityTable<BoggleCompletedRun, 'id'>
  playerAchievementState: EntityTable<PlayerAchievementStateRow, 'id'>
  unviewedAchievementTiers: EntityTable<BoggleUnviewedAchievementTier, 'tierId'>
  unviewedBadges: EntityTable<BoggleUnviewedBadge, 'badgeId'>
  unviewedWords: EntityTable<BoggleUnviewedWord, 'normalizedWord'>
  wordOccurrences: EntityTable<BoggleWordOccurrence, 'id'>
  wordStats: EntityTable<BoggleWordStat, 'normalizedWord'>
}

function createBoggleProgressDatabase() {
  const db = new Dexie(BOGGLE_PROGRESS_DATABASE_NAME) as BoggleProgressDatabase

  // Keep projections disposable: schema v1 stores durable run facts plus the two
  // query projections Words needs now. Future migrations can rebuild the latter.
  db.version(1).stores({
    completedRuns: '&id, completedAt, gameModeId, runKind, ratingId',
    wordOccurrences:
      '&id, runId, normalizedWord, playedAt, [gameModeId+runKind], ratingId',
    wordStats: '&normalizedWord, firstPlayedAt, lastPlayedAt, highestRatingId, length',
  })

  // Schema v2 extends durable completed runs with optional earnedBadges facts
  // for newly written records and adds a disposable badge-progress projection.
  // Existing v1 runs remain valid; rebuilds interpret missing earnedBadges as
  // "no recorded awards" instead of retroactively re-evaluating them.
  db.version(2).stores({
    badgeProgress: '&id',
    completedRuns: '&id, completedAt, gameModeId, runKind, ratingId',
    wordOccurrences:
      '&id, runId, normalizedWord, playedAt, [gameModeId+runKind], ratingId',
    wordStats: '&normalizedWord, firstPlayedAt, lastPlayedAt, highestRatingId, length',
  })

  // Schema v3 extends durable completed runs with an optional row-ready summary
  // snapshot for Run History. Existing v1/v2 runs remain valid and intentionally
  // render as legacy rows when the snapshot is absent.
  db.version(3).stores({
    badgeProgress: '&id',
    completedRuns: '&id, completedAt, gameModeId, runKind, ratingId',
    wordOccurrences:
      '&id, runId, normalizedWord, playedAt, [gameModeId+runKind], ratingId',
    wordStats: '&normalizedWord, firstPlayedAt, lastPlayedAt, highestRatingId, length',
  })

  // Schema v4 widens durable completed runs with neutral accepted-word/action
  // timelines and richer immutable summary totals for future detail/stat views.
  // Existing rows remain valid because all new persisted fields are optional.
  db.version(4).stores({
    badgeProgress: '&id',
    completedRuns: '&id, completedAt, gameModeId, runKind, ratingId',
    wordOccurrences:
      '&id, runId, normalizedWord, playedAt, [gameModeId+runKind], ratingId',
    wordStats: '&normalizedWord, firstPlayedAt, lastPlayedAt, highestRatingId, length',
  })

  // Schema v5 adds setup snapshots, terminal run-end facts, and explicit
  // physical/virtual/effective gold fact splits. Indexes remain unchanged.
  db.version(5).stores({
    badgeProgress: '&id',
    completedRuns: '&id, completedAt, gameModeId, runKind, ratingId',
    wordOccurrences:
      '&id, runId, normalizedWord, playedAt, [gameModeId+runKind], ratingId',
    wordStats: '&normalizedWord, firstPlayedAt, lastPlayedAt, highestRatingId, length',
  })

  // Schema v6 stores durable new/improved badge and new-word markers until the
  // compendium or lexicon is opened (rows are deleted on acknowledge).
  db.version(6).stores({
    badgeProgress: '&id',
    completedRuns: '&id, completedAt, gameModeId, runKind, ratingId',
    unviewedBadges: '&badgeId, kind, ratingId',
    unviewedWords: '&normalizedWord',
    wordOccurrences:
      '&id, runId, normalizedWord, playedAt, [gameModeId+runKind], ratingId',
    wordStats: '&normalizedWord, firstPlayedAt, lastPlayedAt, highestRatingId, length',
  })

  // Schema v7 adds a single durable achievement-state row. Progress remains
  // rebuildable from completed runs plus badge/word projections.
  db.version(7).stores({
    badgeProgress: '&id',
    completedRuns: '&id, completedAt, gameModeId, runKind, ratingId',
    playerAchievementState: '&id',
    unviewedBadges: '&badgeId, kind, ratingId',
    unviewedWords: '&normalizedWord',
    wordOccurrences:
      '&id, runId, normalizedWord, playedAt, [gameModeId+runKind], ratingId',
    wordStats: '&normalizedWord, firstPlayedAt, lastPlayedAt, highestRatingId, length',
  })

  // Schema v8 stores durable achievement-tier markers until Achievements is
  // opened. Rows mirror badge/word unviewed handling: delete on acknowledge.
  db.version(8).stores({
    badgeProgress: '&id',
    completedRuns: '&id, completedAt, gameModeId, runKind, ratingId',
    playerAchievementState: '&id',
    unviewedAchievementTiers: '&tierId',
    unviewedBadges: '&badgeId, kind, ratingId',
    unviewedWords: '&normalizedWord',
    wordOccurrences:
      '&id, runId, normalizedWord, playedAt, [gameModeId+runKind], ratingId',
    wordStats: '&normalizedWord, firstPlayedAt, lastPlayedAt, highestRatingId, length',
  })

  return db
}

export class DexieBoggleLocalProgressStore implements BoggleLocalProgressStore {
  private db: BoggleProgressDatabase

  constructor(db = createBoggleProgressDatabase()) {
    this.db = db
  }

  async clearUnviewedBadges() {
    await this.db.unviewedBadges.clear()
  }

  async clearUnviewedAchievementTiers() {
    await this.db.unviewedAchievementTiers.clear()
  }

  async clearUnviewedWords() {
    await this.db.unviewedWords.clear()
  }

  async getBadgeProgress(): Promise<BoggleBadgeProgressProjection | null> {
    const row = await this.db.badgeProgress.get(BOGGLE_BADGE_PROGRESS_PROJECTION_ID)
    return row
      ? {
          perBadge: row.perBadge,
          profileRunsByRating: row.profileRunsByRating,
        }
      : null
  }

  async getCompletedRun(id: string) {
    return this.db.completedRuns.get(id)
  }

  async getCompletedRuns() {
    return this.db.completedRuns.toArray()
  }

  async getPlayerAchievementState(): Promise<PlayerAchievementState | null> {
    const row = await this.db.playerAchievementState.get(
      BOGGLE_PLAYER_ACHIEVEMENT_STATE_ID,
    )
    return row
      ? {
          backfilledTierIds: row.backfilledTierIds ?? [],
          revealedRatingIds: row.revealedRatingIds ?? [],
          schemaVersion: row.schemaVersion,
          unlockedTierIds: row.unlockedTierIds ?? [],
        }
      : null
  }

  async getUnviewedBadges() {
    return this.db.unviewedBadges.toArray()
  }

  async getUnviewedAchievementTiers() {
    return this.db.unviewedAchievementTiers.toArray()
  }

  async getUnviewedWords() {
    return this.db.unviewedWords.toArray()
  }

  async getWordOccurrences() {
    return this.db.wordOccurrences.toArray()
  }

  async getWordStats() {
    return this.db.wordStats.toArray()
  }

  async putCompletedRun(run: BoggleCompletedRun) {
    await this.db.completedRuns.put(run)
  }

  async putPlayerAchievementState(state: PlayerAchievementState) {
    await this.db.playerAchievementState.put({
      id: BOGGLE_PLAYER_ACHIEVEMENT_STATE_ID,
      ...state,
    })
  }

  async replaceBadgeProgressProjection(projection: BoggleBadgeProgressProjection) {
    await this.db.badgeProgress.put({
      id: BOGGLE_BADGE_PROGRESS_PROJECTION_ID,
      ...projection,
    })
  }

  async replaceUnviewedAchievementTiers(
    entries: readonly BoggleUnviewedAchievementTier[],
  ) {
    await this.db.transaction('rw', this.db.unviewedAchievementTiers, async () => {
      await this.db.unviewedAchievementTiers.clear()
      if (entries.length > 0) {
        await this.db.unviewedAchievementTiers.bulkPut([...entries])
      }
    })
  }

  async replaceWordProjections(projections: BoggleWordProjectionSnapshot) {
    await this.db.transaction(
      'rw',
      this.db.wordOccurrences,
      this.db.wordStats,
      async () => {
        await this.db.wordOccurrences.clear()
        await this.db.wordStats.clear()
        await this.db.wordOccurrences.bulkPut([...projections.wordOccurrences])
        await this.db.wordStats.bulkPut([...projections.wordStats])
      },
    )
  }

  async replaceUnviewedBadges(entries: readonly BoggleUnviewedBadge[]) {
    await this.db.transaction('rw', this.db.unviewedBadges, async () => {
      await this.db.unviewedBadges.clear()
      if (entries.length > 0) {
        await this.db.unviewedBadges.bulkPut([...entries])
      }
    })
  }

  async replaceUnviewedWords(entries: readonly BoggleUnviewedWord[]) {
    await this.db.transaction('rw', this.db.unviewedWords, async () => {
      await this.db.unviewedWords.clear()
      if (entries.length > 0) {
        await this.db.unviewedWords.bulkPut([...entries])
      }
    })
  }
}
